import { useRef, useState } from 'react';
import { api, loadSession, sizeLabel, usd } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';

const CATEGORIES = ['spirits', 'wine', 'beer', 'rtd', 'cider', 'non_alcoholic', 'accessory'];

export default function StockCounts() {
  const counts = useLoad(() => api<any[]>('/api/stock-counts'), []);
  const [open, setOpen] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const isManager = loadSession()?.user.role !== 'cashier';
  if (open) return <CountScreen id={open} onBack={() => { setOpen(null); counts.reload(); }} />;
  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>Stock counts</h1><p>Count the shelves, review what is over or short, then post it. Posting fixes your stock levels and books any loss as shrink.</p></div>
        {isManager && <button className="primary" onClick={() => setStarting(true)}>Start a count</button>}
      </div>
      <ErrorNote error={counts.error} />
      <div className="panel"><div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Count</th><th>Area</th><th className="n">Items counted</th><th>Status</th><th>Started</th></tr></thead>
          <tbody>{counts.data?.map((c) => (
            <tr key={c.id} className="clickable" onClick={() => setOpen(c.id)}>
              <td style={{ fontWeight: 600 }}>{c.name}</td><td>{c.category ? c.category.replace('_', ' ') : 'Whole store'}</td><td className="n">{c.lines}</td>
              <td><span className={`chip ${c.status === 'open' ? 'warn' : c.status === 'posted' ? 'good' : ''}`}>{c.status}</span></td>
              <td>{new Date(c.created_at).toLocaleDateString()}</td>
            </tr>))}</tbody>
        </table>
        {counts.data?.length === 0 && <div className="empty">No counts yet.</div>}
      </div></div>
      {starting && <StartCount onClose={() => setStarting(false)} onDone={(id) => { setStarting(false); setOpen(id); }} />}
    </div>
  );
}

function StartCount({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [name, setName] = useState(`Count ${new Date().toLocaleDateString()}`);
  const [category, setCategory] = useState('');
  const [err, setErr] = useState<unknown>(null);
  return (
    <Modal title="Start a stock count" onClose={onClose} footer={<button className="primary" disabled={!name.trim()} onClick={async () => {
      try { onDone((await api<any>('/api/stock-counts', { body: { name, category: category || null } })).id); } catch (e) { setErr(e); }
    }}>Start</button>}>
      <ErrorNote error={err} />
      <label>Name<input autoFocus value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label>Area<select value={category} onChange={(e) => setCategory(e.target.value)}>
        <option value="">Whole store</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c.replace('_', ' ')} only</option>)}
      </select></label>
    </Modal>
  );
}

function CountScreen({ id, onBack }: { id: string; onBack: () => void }) {
  const count = useLoad(() => api<any>(`/api/stock-counts/${id}`), [id]);
  const [code, setCode] = useState('');
  const [last, setLast] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [posting, setPosting] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const isManager = loadSession()?.user.role !== 'cashier';
  const d = count.data;
  const open = d?.status === 'open';

  async function scan(e: React.FormEvent) {
    e.preventDefault();
    const c = code.trim(); setCode(''); setErr(null);
    if (!c) return;
    try {
      const r = await api<any>(`/api/stock-counts/${id}/lines`, { body: { code: c, qty: 1, mode: 'add' } });
      setLast(`${r.brand} ${r.name}: counted ${r.counted} (expected ${r.expected})`);
      count.reload();
    } catch (e2) { setErr(e2); }
    input.current?.focus();
  }
  async function setQty(itemId: string, qty: number) {
    setErr(null);
    try { await api(`/api/stock-counts/${id}/lines`, { body: { itemId, qty, mode: 'set' } }); count.reload(); } catch (e) { setErr(e); }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>{d?.name ?? 'Stock count'}</h1><p>{d?.category ? `${d.category.replace('_', ' ')} only` : 'Whole store'} · {d?.status}</p></div>
        <button onClick={onBack}>Back</button>
        {open && isManager && <button onClick={async () => { if (confirm('Cancel this count? Nothing will change in your stock.')) { await api(`/api/stock-counts/${id}/cancel`, { body: {} }); onBack(); } }}>Cancel count</button>}
        {open && isManager && <button className="primary" disabled={!d?.lines.length} onClick={() => setPosting(true)}>Review and post</button>}
      </div>
      <ErrorNote error={err ?? count.error} />
      {open && (
        <form className="scanbar" onSubmit={scan}>
          <input ref={input} autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="Scan each bottle (or a case barcode)" aria-label="Scan to count" />
          <button className="primary big">Count</button>
        </form>
      )}
      {last && <div className="notice good">{last}</div>}
      {d && (
        <div className="stats">
          <div><div className="v">{d.summary.counted}</div><div className="l">Items counted</div></div>
          <div><div className="v">{d.summary.unitsOver}</div><div className="l">Units over</div></div>
          <div><div className="v">{d.summary.unitsShort}</div><div className="l">Units short</div></div>
          <div><div className="v">{usd(d.summary.netValueCents)}</div><div className="l">Net value change</div></div>
        </div>
      )}
      <div className="panel"><div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Product</th><th className="n">Expected</th><th className="n">Counted</th><th className="n">Variance</th></tr></thead>
          <tbody>{d?.lines.map((l: any) => (
            <tr key={l.item_id}>
              <td>{l.brand} {l.name} <span className="faint">{sizeLabel(l.size_ml)}</span></td>
              <td className="n">{l.expected_qty}</td>
              <td className="n">{open ? <input style={{ width: 70, textAlign: 'right' }} inputMode="numeric" defaultValue={l.counted_qty} onBlur={(e) => { const v = Number(e.target.value.replace(/\D/g, '')); if (v !== l.counted_qty) setQty(l.item_id, v); }} /> : l.counted_qty}</td>
              <td className="n" style={{ color: l.variance < 0 ? 'var(--bad, #b3261e)' : undefined, fontWeight: l.variance ? 600 : undefined }}>{l.variance > 0 ? '+' : ''}{l.variance}</td>
            </tr>))}</tbody>
        </table>
        {d?.lines.length === 0 && <div className="empty">Nothing counted yet. Scan the first bottle.</div>}
      </div></div>
      {posting && d && (
        <Modal title="Post this count?" onClose={() => setPosting(false)} footer={<button className="primary" onClick={async () => {
          try { await api(`/api/stock-counts/${id}/post`, { body: {} }); setPosting(false); count.reload(); } catch (e) { setErr(e); setPosting(false); }
        }}>Post count</button>}>
          <p>{d.summary.counted} items counted: <b>{d.summary.unitsOver} units over</b>, <b>{d.summary.unitsShort} units short</b>, a net change of <b>{usd(d.summary.netValueCents)}</b> at cost.</p>
          <p className="muted">Items you did not scan are left as they are. Posting cannot be undone.</p>
        </Modal>
      )}
    </div>
  );
}
